import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Space, message, Typography, Popconfirm, Select, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from '../services/axiosConfig';
import { SERVICE_API } from '../constants/api';

const { Title } = Typography;

const ServiceManagement = () => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        setLoading(true);
        try {
            const response = await axios.get(SERVICE_API.LIST);
            if (response.data && response.data.success) {
                setServices(response.data.data || []);
            } else {
                setServices([]);
            }
        } catch (error) {
            message.error('Lỗi khi tải dữ liệu dịch vụ');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingService) {
                await axios.put(SERVICE_API.UPDATE(editingService.id), values);
                message.success('Cập nhật dịch vụ thành công');
            } else {
                await axios.post(SERVICE_API.CREATE, values);
                message.success('Thêm dịch vụ thành công');
            }
            setIsModalVisible(false);
            form.resetFields();
            fetchServices();
        } catch (error) {
            message.error('Lỗi khi lưu dịch vụ');
        }
    };

    const deleteService = async (id) => {
        try {
            await axios.delete(SERVICE_API.DELETE(id));
            message.success('Xóa dịch vụ thành công');
            fetchServices();
        } catch (error) {
            message.error('Lỗi khi xóa dịch vụ');
        }
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 100 },
        { 
            title: 'Icon', 
            dataIndex: 'icon_url', 
            key: 'icon_url',
            width: 80,
            render: (url, record) => (
                <div style={{ backgroundColor: record.bg_color_hex, padding: 8, borderRadius: 4, display: 'inline-block' }}>
                    <img src={url} alt="icon" style={{width: 32, height: 32, filter: 'brightness(0) invert(1)'}} />
                </div>
            )
        },
        { title: 'Tên dịch vụ', dataIndex: 'title', key: 'title' },
        { title: 'Hotline', dataIndex: 'hotline', key: 'hotline', width: 120 },
        { title: 'Trạng thái', dataIndex: 'status', key: 'status', width: 120 },
        {
            title: 'Hành động',
            key: 'action',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button type="primary" icon={<EditOutlined />} onClick={() => {
                        setEditingService(record);
                        form.setFieldsValue({
                            ServiceID: record.id,
                            Title: record.title,
                            IconUrl: record.icon_url,
                            BgColorHex: record.bg_color_hex,
                            Description: record.description,
                            ImageUrl: record.image_url,
                            Hotline: record.hotline,
                            Status: record.status
                        });
                        setIsModalVisible(true);
                    }}>Sửa</Button>
                    <Popconfirm title="Bạn có chắc chắn muốn xóa dịch vụ này?" onConfirm={() => deleteService(record.id)}>
                        <Button danger icon={<DeleteOutlined />}>Xóa</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Card title={<Title level={4}>Quản lý Dịch vụ</Title>}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                setEditingService(null);
                form.resetFields();
                setIsModalVisible(true);
            }} style={{ marginBottom: 16 }}>
                Thêm dịch vụ
            </Button>
            <Table columns={columns} dataSource={services} rowKey="id" loading={loading} scroll={{ x: 800 }} />

            <Modal
                title={editingService ? "Sửa dịch vụ" : "Thêm dịch vụ"}
                visible={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                width={800}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="ServiceID" label="Mã dịch vụ (ServiceID)" rules={[{ required: true }]} tooltip="Ví dụ: srv_005">
                                <Input disabled={!!editingService} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="Title" label="Tên dịch vụ" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="IconUrl" label="Icon URL">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="BgColorHex" label="Mã màu nền (Hex)" tooltip="Ví dụ: #FF5722">
                                <Input type="color" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="Hotline" label="Hotline">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="Status" label="Trạng thái">
                                <Select defaultValue="ACTIVE">
                                    <Select.Option value="ACTIVE">Hoạt động</Select.Option>
                                    <Select.Option value="INACTIVE">Ngừng hoạt động</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="ImageUrl" label="Image URL (Ảnh mô tả)">
                        <Input />
                    </Form.Item>
                    <Form.Item name="Description" label="Mô tả chi tiết">
                        <Input.TextArea rows={4} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default ServiceManagement;
